import { Center, Spinner, Box, Skeleton, VStack, HStack, Flex } from '@chakra-ui/react'

interface LoadingSpinnerProps {
  height?: string
  width?: string
}

/**
 * Standard loading spinner component for Suspense fallbacks.
 */
export function LoadingSpinner({ 
  height = "100%",
  width = "100%"
}: LoadingSpinnerProps): JSX.Element {
  return (
    <Center height={height} width={width}>
      <Spinner
        thickness="4px"
        speed="0.65s"
        emptyColor="appPaleGrey"
        color="appBlue"
        size="xl"
      />
    </Center>
  )
}

/**
 * Full page loading spinner for top-level Suspense boundaries.
 */
export function FullPageLoadingSpinner(): JSX.Element {
  return (
    <Center height="100vh" width="100%">
      <Spinner
        thickness="4px"
        speed="0.65s"
        emptyColor="appPaleGrey"
        color="appBlue"
        size="xl"
      />
    </Center>
  )
}

/**
 * NavBar skeleton loader to prevent layout shift during lazy loading.
 */
export function NavBarSkeleton(): JSX.Element {
  return (
    <Box
      width="260px"
      height="100vh"
      backgroundColor="#0b1b36"
      boxShadow="0 0 10px 0 rgba(0, 0, 0, 0.12)"
      flexShrink={0}
      padding="16px"
      display="flex"
      flexDirection="column"
    >
      {/* Logo */}
      <Flex mb={4} gap="10px" alignItems="center" paddingY="1em">
        <Skeleton height="32px" width="32px" borderRadius="4px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
        <Skeleton height="24px" width="109px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
      </Flex>

      {/* Upgrade box placeholder */}
      <Skeleton 
        height="129px" 
        width="100%" 
        borderRadius="12px" 
        mb={4}
        startColor="rgba(202,217,239,0.15)" 
        endColor="rgba(202,217,239,0.25)" 
      />

      <VStack spacing={0} align="stretch" flex={1}>
        {/* Static navigation items (Home, Usage Analytics, Purge Cache) */}
        <Box borderTop="1px solid #4B5A74" py={4}>
          <VStack spacing={1} align="stretch">
            <HStack spacing={3} height="36px" alignItems="center" px={3}>
              <Skeleton height="20px" width="20px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
              <Skeleton height="16px" width="80px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
            </HStack>
            <HStack spacing={3} height="36px" alignItems="center" px={3}>
              <Skeleton height="20px" width="20px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
              <Skeleton height="16px" width="120px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
            </HStack>
            <HStack spacing={3} height="36px" alignItems="center" px={3}>
              <Skeleton height="20px" width="20px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
              <Skeleton height="16px" width="100px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
            </HStack>
          </VStack>
        </Box>

        {/* Section 1: DIGITAL ASSET MANAGEMENT */}
        <Box borderTop="1px solid #4B5A74" py={4}>
          <Skeleton height="12px" width="180px" mb={2} startColor="rgba(205,213,223,0.2)" endColor="rgba(205,213,223,0.3)" />
          <VStack spacing={1} align="stretch">
            <HStack spacing={3} height="36px" alignItems="center" px={3}>
              <Skeleton height="20px" width="20px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
              <Skeleton height="16px" width="110px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
            </HStack>
            <HStack spacing={3} height="36px" alignItems="center" px={3}>
              <Skeleton height="20px" width="20px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
              <Skeleton height="16px" width="130px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
            </HStack>
            <HStack spacing={3} height="36px" alignItems="center" px={3}>
              <Skeleton height="20px" width="20px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
              <Skeleton height="16px" width="115px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
            </HStack>
            <HStack spacing={3} height="36px" alignItems="center" px={3}>
              <Skeleton height="20px" width="20px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
              <Skeleton height="16px" width="90px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
            </HStack>
            <HStack spacing={3} height="36px" alignItems="center" px={3}>
              <Skeleton height="20px" width="20px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
              <Skeleton height="16px" width="85px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
            </HStack>
          </VStack>
        </Box>

        {/* Section 2: CONFIGURATION */}
        <Box borderTop="1px solid #4B5A74" py={4}>
          <Skeleton height="12px" width="120px" mb={2} startColor="rgba(205,213,223,0.2)" endColor="rgba(205,213,223,0.3)" />
          <VStack spacing={1} align="stretch">
            <HStack spacing={3} height="36px" alignItems="center" px={3}>
              <Skeleton height="20px" width="20px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
              <Skeleton height="16px" width="120px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
            </HStack>
            <HStack spacing={3} height="36px" alignItems="center" px={3}>
              <Skeleton height="20px" width="20px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
              <Skeleton height="16px" width="110px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
            </HStack>
            <HStack spacing={3} height="36px" alignItems="center" px={3}>
              <Skeleton height="20px" width="20px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
              <Skeleton height="16px" width="75px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
            </HStack>
          </VStack>
        </Box>

        <Box flex={1} />

        {/* User profile at bottom */}
        <HStack spacing={3} pb={2}>
          <Skeleton height="44px" width="44px" borderRadius="full" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
          <VStack spacing={2} align="start" flex={1}>
            <Skeleton height="14px" width="110px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
            <Skeleton height="12px" width="150px" startColor="rgba(255,255,255,0.1)" endColor="rgba(255,255,255,0.2)" />
          </VStack>
        </HStack>
      </VStack>
    </Box>
  )
}

